import { CancelButton, SaveButton } from '@core/components/Buttons';
import { SimpleDialog, SimpleDialogContent } from '@core/components/Dialog';
import ImportErrorTable from '@core/components/ImportModal/ImportErrorTable';
import ImportModal from '@core/components/ImportModal/ImportModal';
import { useReduxLoading } from '@core/hooks';
import { useApiUtils } from '@core/util/apiUtils';
import { downloadFile } from '@core/util/downloadUtils';
import { mensagemCampoObrigatorio } from '@core/util/mensagemUtil';
import FuseUtils from '@fuse/utils';
import contasReceberApi from 'app/services/api/contaReceberApi';
import * as Actions from 'app/store/actions';
import { eventReactGA } from 'app/utils/analyticsUtil';
import { FormikProvider, useFormik } from 'formik';
import React, { useState } from 'react';
import { FaArrowCircleUp } from 'react-icons/fa';
import { IoIosAlert } from 'react-icons/io';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import { ContaReceberContext } from '../../contaReceberContext';
import { contaReceberModais } from './contaReceberModais';
const CONTA_RECEBER = 'Conta a receber';

const ImportarContaReceberModal = ({ fetchContasReceber }) => {
  const context = React.useContext(ContaReceberContext);
  const [abrirImportModal, setAbrirImportModal] = context.modal;
  const [arquivo, setArquivo] = useState(null);
  const [errorData, setErrorData] = useState([]);
  const [error, setError] = useState(false);
  const [idNotificacao, setIdNotificacao] = useState();
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [progress, setProgress] = useState(0);
  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  const extensoesPermitidas = ['xlsx'];
  const titulo = error ? 'Erros identificados na importação' : 'Importar planilha de contas a receber';

  const showMessage = (message, variant) => {
    dispatch(
      Actions.showMessage({
        message,
        variant,
      })
    );
  };

  useReduxLoading(loading);

  const handleClose = () => {
    setArquivo('');
    setDisabled(false);
    if (error) {
      setError(false);
      setErrorData([]);
    } else {
      fetchContasReceber();
      setAbrirImportModal(false);
      formik.resetForm(formik.initialValues);
    }
  };

  const handleUpload = (file) => {
    if (file) {
      try {
        const chaveNotificacao = FuseUtils.generateGUID();
        setIdNotificacao(chaveNotificacao);
        setDisabled(true);

        const arquivo = Object.assign(file, {
          id: undefined,
          nome: file.name,
          contentType: file.type,
        });

        const extensao = arquivo.nome.substring(arquivo.nome.lastIndexOf('.') + 1);

        if (!extensoesPermitidas.includes(extensao.toLowerCase())) {
          setArquivo('');
          showMessage('É permitido anexar somente arquivos no formato .xlsx', 'error');
          setDisabled(false);
          setProgress(0);
          setLoading(false);
        } else {
          const formData = new FormData();
          formData.append('arquivo', arquivo);

          setLoading(true);
          eventReactGA(CONTA_RECEBER, 'Importar planilha');
          contasReceberApi.importar(formData, chaveNotificacao);
        }
      } catch (error) {
        apiUtils.handleApiError(error);
      }
    }
  };

  const onFileChange = (event) => {
    const file = event.target.files[0];
    setProgress(0);

    formik.handleChange({
      target: {
        id: 'arquivo',
        value: file,
      },
    });

    setArquivo(file);
  };

  const handleProgressUpdate = (notificacao) => {
    if (idNotificacao === notificacao.id) {
      const mensagem = JSON.parse(notificacao.mensagem);
      const newProgress = parseFloat(mensagem.percentual);
      setProgress(newProgress);
      setLoading(false);

      if (newProgress === 100) {
        if (mensagem?.errors && mensagem?.errors?.length > 0) {
          if (mensagem.errors[0].campo === 'arquivo-error') {
            showMessage(mensagem.errors[0].mensagemErro, 'error');
            setDisabled(false);
            setProgress(0);
          } else if (mensagem.errors[0].campo === 'arquivo-planilha-error') {
            showMessage(mensagem.errors[0].mensagemErro, 'error');
            setDisabled(false);
            setProgress(0);
          } else {
            setError(true);
            setErrorData(mensagem.errors);
          }
        }
        if (mensagem?.errors?.length === 0) {
          showMessage('Importação realizada com sucesso.', 'success');
          handleClose();
        }
      }
    }
  };

  const obterModelo = async () => {
    try {
      setLoading(true);
      eventReactGA(CONTA_RECEBER, 'Obter planilha');
      const response = await contasReceberApi.obterModelo();
      downloadFile(response.data.bytes, response.data.nome);
    } catch (error) {
      apiUtils.handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const ImportarArquivoSchema = yup.object().shape({
    arquivo: yup
      .string()
      .nullable()
      .required(mensagemCampoObrigatorio('o arquivo')),
  });

  const getInitialValues = (registro) => ({
    ...registro,
    id: registro?.id,
    arquivo: registro?.arquivo,
  });

  const formik = useFormik({
    initialValues: getInitialValues(arquivo),
    validationSchema: ImportarArquivoSchema,
    onSubmit: (values) => {
      setLoading(true);
      handleUpload(arquivo);
    },
    validateOnChange: true,
    validateOnBlur: true,
  });

  return (
    <SimpleDialog open={abrirImportModal === contaReceberModais.importarContaReceber} onClose={handleClose} innerScroll>
      <FormikProvider value={formik}>
        <SimpleDialogContent
          header={{
            title: titulo,
            icon: error ? <IoIosAlert /> : <FaArrowCircleUp />,
            toolbar: (
              <>
                <CancelButton title="Cancelar" type="button" onClick={handleClose}>
                  {error ? 'Fechar' : 'Cancelar'}
                </CancelButton>
                {!error && (
                  <SaveButton type="submit" onClick={formik.handleSubmit} disabled={disabled}>
                    {' '}
                    Processar
                  </SaveButton>
                )}
              </>
            ),
          }}
          content={
            <>
              {error ? (
                <ImportErrorTable errors={errorData} />
              ) : (
                <ImportModal
                  onFileChange={onFileChange}
                  arquivo={arquivo}
                  obterModelo={obterModelo}
                  progress={progress}
                  handleProgressUpdate={handleProgressUpdate}
                  formik={formik}
                />
              )}
            </>
          }
          contentStyle={{ padding: 0 }}
        />
      </FormikProvider>
    </SimpleDialog>
  );
};

export default ImportarContaReceberModal;
